import { debounce, ERROR_MESSAGES, FractionError, UserAuthenticationError } from "@fraction/shared";
import {
  type UseMutationOptions as UseMutationOptionsLib,
  type UseQueryOptions as UseQueryOptionsLib,
  useMutation as useMutationLib,
  useQuery as useQueryLib,
} from "@tanstack/react-query";
import type { ReactNode } from "react";
import { toast } from "react-toastify";
import type { ResponseError as ResponseErrorSuperagent } from "superagent";

const GENERIC_ERROR_MESSAGE = "Our server had a hiccup! Please wait and try again in a moment.";

interface AdditionalMutationQueryOptions<TData, TError, TVariables> {
  errorMessage?: string | ((error: TError) => string | undefined | void | ReactNode);
  // Matches the runtime signature in the wrapper below (`onError?.(err, variables, context)`).
  // `context` is whatever the caller's `onMutate` returned — used for optimistic-update rollback.
  onError?: (err: TError, variables: TVariables, context: unknown) => void;
  supersedeOtherErrorsInMs?: number;
  onSuccess?: (data: TData, variables: TVariables, context?: any) => void | Promise<void>;
}

interface AdditionalQueryOptions<TData, TError> {
  errorMessage?: string | ((error: TError) => string | undefined | void);
  onError?: (err: TError, toaster: typeof toast) => void;
  supersedeOtherErrorsInMs?: number;
  onSuccess?: (data: TData, fromCache?: boolean) => void | Promise<void>;
}

export type UseMutationOptions<TData = unknown, TError = any, TVariables = void> = UseMutationOptionsLib<
  TData,
  TError,
  TVariables
> &
  AdditionalMutationQueryOptions<TData, TError, TVariables>;

export type UseQueryOptions<TQueryFnData = unknown, TError = any, TData = TQueryFnData> = Omit<
  UseQueryOptionsLib<TQueryFnData, TError, TData>,
  "onError"
> &
  AdditionalQueryOptions<TData, TError>;

/**
 * A custom error class that wraps response errors from superagent.
 *
 * Note: this constructor must NEVER throw. Network aborts/timeouts (and some
 * in-app webview failures) surface as errors with no `status`/`response`. We used
 * to throw a ValueError here when those were missing, but that destroyed the
 * original error and masked the real failure (FRONTEND-1G3 / 1H3). Instead we
 * fall back to sensible defaults (status 0 = "no HTTP response") and preserve the
 * original error message so the true cause still surfaces.
 */
export class ResponseError extends FractionError {
  public constructor(err: ResponseErrorSuperagent) {
    const body = err?.response?.body ?? {};
    const message = body.message || err?.message || GENERIC_ERROR_MESSAGE;
    super(message, body);

    this.name = body.name || err?.name || "HTTPError";
    this.data = body.data || {};
    this.statusCode = err?.status ?? 0;
  }
}

const recentErrorMessages = new Map<string | ReactNode, number>();
const ERROR_DEDUPE_WINDOW_MS = 5000;

const showToastError = (message: string | ReactNode) => {
  if (typeof document !== "undefined" && document.visibilityState !== "visible") {
    return;
  }
  const now = Date.now();
  const last = recentErrorMessages.get(message);
  if (last !== undefined && now - last < ERROR_DEDUPE_WINDOW_MS) {
    return;
  }
  recentErrorMessages.set(message, now);
  if (recentErrorMessages.size > 50) {
    for (const [key, timestamp] of recentErrorMessages) {
      if (now - timestamp >= ERROR_DEDUPE_WINDOW_MS) {
        recentErrorMessages.delete(key);
      }
    }
  }
  toast.error(message);
};

const debouncedToastError = debounce(showToastError, 1000);

/*
 * Error message order of precedence:
 * 1. If errorMessage is a function and it returns a non-empty string, use that
 * 2. If errorMessage is a non-empty string, use that
 * 3. If errorMessage is empty/undefined, use the generic error message
 *
 * If errorMessage is or returns an empty string don't show any error message
 */
export const defaultErrorMessageHandler = <TError extends Error>(
  error: TError,
  errorMessage?: string | ((error: TError) => string | undefined | void | ReactNode)
) => {
  const customErrorMessage =
    (errorMessage instanceof Function ? errorMessage?.(error) : errorMessage) || error?.message;

  if (customErrorMessage === "") {
    return;
  }

  if (typeof customErrorMessage === "string" && customErrorMessage?.includes("Request has been terminated")) {
    return;
  }

  if (
    window.location.pathname === "/login" &&
    error instanceof UserAuthenticationError &&
    error.message !== ERROR_MESSAGES.INCORRECT_PASSWORD
  ) {
    // this is an auth error but we have been redirected to the login page, so just ignore.
    return;
  }
  console.error(error);
  debouncedToastError(customErrorMessage || GENERIC_ERROR_MESSAGE);
};

export const useQuery = <TQueryFnData = unknown, TError = any, TData = TQueryFnData>(
  options: UseQueryOptions<TQueryFnData, TError, TData>
) => {
  return useQueryLib({
    ...options,
    queryFn: async () => {
      // @ts-expect-error
      const result = await options.queryFn?.();
      if (options.onSuccess) {
        await options.onSuccess?.(result, false);
      }
      return result;
    },
  });
};

export const useMutation = <TData = unknown, TError extends Error = any, TVariables = void>(
  options: UseMutationOptions<TData, TError, TVariables>
) => {
  const { errorMessage, onError, ...reactQueryOptions } = options;

  return useMutationLib<TData, TError, TVariables>({
    onError: (err, variables, context) => {
      onError?.(err, variables, context);
      if (!onError) {
        defaultErrorMessageHandler<TError>(err, errorMessage);
      }
    },
    ...reactQueryOptions,
  });
};

/**
 * Decorate a function so any ResponseErrors from superagent are wrapped and re-thrown as our custom
 * ResponseError object.
 *
 * This is necessary since, for some reason, react-query sometimes doesn't handle errors from superagent
 * nicely. I was experiencing errors being thrown but the onError react-query handler wasn't being called.
 * Wrapping and re-throwing the error fixed this issue.
 *
 * Note: the `ResponseError` constructor tolerates errors with no status/response
 * (e.g. network aborts/timeouts), preserving the original message instead of
 * throwing — so the real failure is never masked.
 */
export const wrapSuperagentErrors =
  <Args extends any[], Result>(
    fn: (...args: Args) => Promise<Result>
  ): ((...args: Args) => Promise<Result>) =>
  async (...args: Args): Promise<Result> => {
    try {
      return await fn(...args);
    } catch (err) {
      throw new ResponseError(err);
    }
  };
